
import { defineComponent, PropType } from 'vue'
import { iPerspective, iScorecardTarget } from '@/modules/managers/scorecards/Scorecards'
import { AxiosResponse } from 'axios'
import Card from 'primevue/card'

const deepEqual = require('deep-equal')

export default defineComponent({
    name: 'kn-perspective-card',
    components: { Card },
    props: { propPerspective: { type: Object as PropType<iPerspective> } },
    data() {
        return {
            perspective: null as iPerspective | null,
            toolbarBorderClass: 'perspective-toolbar-light-grey'
        }
    },
    computed: {
        perspectiveUpdated(): boolean | undefined {
            return this.perspective?.updated
        }
    },
    watch: {
        propPerspective() {
            this.loadPerspective()
        },
        async perspectiveUpdated(value: boolean) {
            if (value && this.perspective) {
                await this.evaluatePerspective()
                this.evaluatePerspectiveTargets()
                this.perspective.updated = false
            }
        }
    },
    created() {
        this.loadPerspective()
    },
    methods: {
        async loadPerspective() {
            this.perspective = this.propPerspective ? this.propPerspective : ({} as iPerspective)

            if (this.perspective && this.perspective.criterion.valueId) {
                await this.evaluatePerspective()
                this.evaluatePerspectiveTargets()
            }
        },
        getTargetIconLetter(criterionValue: string | null) {
            if (!criterionValue) criterionValue = ''

            switch (criterionValue) {
                case 'MAJORITY':
                    return 'M'
                case 'MAJORITY_WITH_PRIORITY':
                    return 'MP'
                case 'PRIORITY':
                    return 'P'
                default:
                    return ''
            }
        },
        async evaluateCriteria(criterionId: number, statusArray: any[], target: iScorecardTarget | null) {
            this.$store.commit('setLoading', true)
            await this.$http
                .post(process.env.VUE_APP_RESTFUL_SERVICES_PATH + `1.0/kpiee/${criterionId}/evaluateCriterion`, statusArray)
                .then((response: AxiosResponse<any>) => {
                    if (!target && this.perspective) {
                        this.perspective.statusColor = response.data ? response.data.status : null
                        this.setPerspectiveToolbarClass()
                    } else if (target) {
                        target.statusColor = response.data ? response.data.status : null
                    }
                })
                .catch(() => {})
            this.$store.commit('setLoading', false)
        },
        async evaluatePerspective() {
            if (!this.perspective) return

            for (let i = 0; i < this.perspective.targets.length; i++) {
                this.addGroupedTargets(this.perspective.targets[i].status)
                this.addGroupedKpiItems(this.perspective.targets[i])
            }

            const statusArray = [] as any[]
            for (let i = 0; i < this.perspective.targets.length; i++) {
                statusArray.push({ status: this.perspective.targets[i].status, priority: false })
            }

            for (let i = 0; i < this.perspective.options.criterionPriority.length; i++) {
                for (let j = 0; j < this.perspective.targets.length; j++) {
                    if (this.perspective.options.criterionPriority[i] === this.perspective.targets[j].name) {
                        statusArray[j].priority = true
                    }
                }
            }

            await this.evaluateCriteria(this.perspective.criterion.valueId, statusArray, null)
        },
        evaluatePerspectiveTargets() {
            if (!this.perspective) return
            this.perspective.targets?.forEach((target: iScorecardTarget) => {
                if (target.updated || target.updated === undefined) {
                    this.evaluateTarget(target)
                    target.updated = false
                }
            })
        },
        async evaluateTarget(target: iScorecardTarget) {
            const statusArray = [] as any[]
            for (let i = 0; i < target.kpis.length; i++) {
                statusArray.push({ status: target.kpis[i].status, priority: false })
            }

            for (let i = 0; i < target.options.criterionPriority.length; i++) {
                for (let j = 0; j < target.kpis.length; j++) {
                    if (target.options.criterionPriority[i] === target.kpis[j].name) {
                        statusArray[j].priority = true
                    }
                }
            }

            await this.evaluateCriteria(target.criterion.valueId, statusArray, target)
        },
        addGroupedTargets(type: any) {
            if (!this.perspective || !this.perspective.groupedTargets) return

            for (let i = 0; i < this.perspective.groupedTargets?.length; i++) {
                if (deepEqual(this.perspective.groupedTargets[i].status, type)) {
                    this.perspective.groupedTargets[i].count++
                    break
                }
            }
            this.perspective?.groupedTargets?.push({ status: type, count: 1 })
        },
        addGroupedKpiItems(target: iScorecardTarget) {
            if (!this.perspective || !target.groupedKpis) return

            if (!this.perspective.groupedKpis) {
                this.perspective.groupedKpis = []
            }

            for (let i = 0; i < target.groupedKpis.length; i++) {
                const tempGroupedKpis = target.groupedKpis[i]
                let found = false

                for (let j = 0; j < this.perspective?.groupedKpis?.length; j++) {
                    if (deepEqual(this.perspective.groupedKpis[j].status, tempGroupedKpis.status)) {
                        this.perspective.groupedKpis[j].count += tempGroupedKpis.count
                        found = true
                        break
                    }
                }
                if (!found) {
                    this.perspective.groupedKpis?.push({ status: tempGroupedKpis.status, count: tempGroupedKpis.count })
                }
            }
        },
        setPerspectiveToolbarClass() {
            if (this.perspective?.statusColor) {
                switch (this.perspective.statusColor) {
                    case 'RED':
                        this.toolbarBorderClass = 'perspective-toolbar-red'
                        break
                    case 'YELLOW':
                        this.toolbarBorderClass = 'perspective-toolbar-yellow'
                        break
                    case 'GREEN':
                        this.toolbarBorderClass = 'perspective-toolbar-green'
                        break
                    case 'GREY':
                        this.toolbarBorderClass = 'perspective-toolbar-grey'
                }
            } else {
                this.toolbarBorderClass = 'perspective-toolbar-light-grey'
            }
        },
        getTargetStatusIconColor(target: iScorecardTarget) {
            if (target?.statusColor) {
                switch (target.statusColor) {
                    case 'RED':
                        return 'scorecard-icon-red'
                    case 'YELLOW':
                        return 'scorecard-icon-yellow'
                    case 'GREEN':
                        return 'scorecard-icon-green'
                    case 'GREY':
                        return 'scorecard-icon-grey'
                }
            } else {
                return 'scorecard-icon-light-grey'
            }
        },
        getSelectedCriteriaTooltip(criterionValue: string | null) {
            switch (criterionValue) {
                case 'MAJORITY':
                    return this.$t('managers.scorecards.majority')
                case 'MAJORITY_WITH_PRIORITY':
                    return this.$t('managers.scorecards.majorityWithPriority')
                case 'PRIORITY':
                    return this.$t('managers.scorecards.priority')
                default:
                    return ''
            }
        },
        getPriorityItems(item: iPerspective | iScorecardTarget) {
            let targets = ''
            if (item && item.options) {
                for (let i = 0; i < item.options.criterionPriority.length; i++) {
                    targets += item.options.criterionPriority[i]
                    targets += i === item.options.criterionPriority.length - 1 ? ' ' : ', '
                }
            }

            return targets
        }
    }
})
